import type { ChatRoom } from "@talkers/shared";
import { createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { CatAvatar } from "@/components/CatAvatar";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  EmptyState,
  HStack,
  Skeleton,
  Spinner,
  Stack,
  TextArea,
} from "@/components/ui";
import { relativeTime } from "@/lib/time";
import {
  activeRoomId,
  closeChat,
  messages,
  messagesLoading,
  rooms,
  roomsLoading,
  selectRoom,
  sendMessage,
} from "@/state/chat";
import { isAdmin, me } from "@/state/me";
import { sendKeyLabel, shouldSend } from "@/state/settings";

function roomTitle(room: ChatRoom, myId: number | undefined): string {
  const others = room.members.filter((member) => member.id !== myId);
  return others.map((member) => member.name).join(", ") || "自分";
}

export function ChatDialog(): JSX.Element {
  const [body, setBody] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [sending, setSending] = createSignal(false);

  const current = createMemo(() => {
    const id = activeRoomId();
    return id === null ? [] : (messages[id] ?? []);
  });

  const submit = async () => {
    const roomId = activeRoomId();
    const text = body().trim();
    if (roomId === null || text === "" || sending()) return;
    setSending(true);
    setError(null);
    try {
      await sendMessage(roomId, text);
      setBody("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "送信できませんでした");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={closeChat} size="lg">
      <DialogHeader>チャット</DialogHeader>
      <DialogBody>
        <div class="chat">
          <div class="chat-rooms">
            <Show
              when={!roomsLoading()}
              fallback={
                <Stack gap={2}>
                  <Skeleton height="2.5rem" variant="rect" />
                  <Skeleton height="2.5rem" variant="rect" />
                  <Skeleton height="2.5rem" variant="rect" />
                </Stack>
              }
            >
              <Show when={rooms().length > 0} fallback={<p class="online-empty">まだありません</p>}>
                <For each={rooms()}>
                  {(room) => (
                    <button
                      type="button"
                      class="chat-room"
                      classList={{ "chat-room--active": activeRoomId() === room.id }}
                      onClick={() => void selectRoom(room.id)}
                    >
                      <span class="chat-room-title">{roomTitle(room, me()?.id)}</span>
                      <Show when={room.lastMessageAt}>
                        {(at) => <span class="chat-room-time">{relativeTime(at())}</span>}
                      </Show>
                    </button>
                  )}
                </For>
              </Show>
            </Show>
          </div>

          <div class="chat-main">
            <Show
              when={activeRoomId() !== null}
              fallback={
                // 一覧を取る前に「選んでください」を出すと、直後に会話へ切り替わって二度手間に見える
                <Show
                  when={!roomsLoading()}
                  fallback={
                    <div class="chat-loading">
                      <Spinner label="読み込み中" />
                    </div>
                  }
                >
                  <EmptyState
                    title="相手を選んでください"
                    description="左の一覧から選ぶか、オンラインの人をクリックします"
                  />
                </Show>
              }
            >
              <Stack gap={2}>
                <Show when={error()}>
                  {(message) => <Alert variant="danger">{message()}</Alert>}
                </Show>
                <div class="chat-log">
                  <Show
                    when={!messagesLoading()}
                    fallback={
                      <div class="chat-loading">
                        <Spinner label="読み込み中" />
                      </div>
                    }
                  >
                    <Show
                      when={current().length > 0}
                      fallback={<p class="chat-empty">まだメッセージがありません</p>}
                    >
                      <For each={current()}>
                        {(message) => (
                          <HStack
                            gap={2}
                            align="start"
                            class="chat-message"
                            classList={{ "chat-message--mine": message.sender.userId === me()?.id }}
                          >
                            <CatAvatar
                              avatar={message.sender.avatar}
                              size={28}
                              label={message.sender.name}
                            />
                            <div>
                              <div class="chat-message-meta">
                                {message.sender.name}
                                <Show when={message.sender.isBot && isAdmin()}> (bot)</Show> ·{" "}
                                {relativeTime(message.createdAt)}
                              </div>
                              <div class="chat-message-body">{message.body}</div>
                            </div>
                          </HStack>
                        )}
                      </For>
                    </Show>
                  </Show>
                </div>
                <div
                  onKeyDown={(event) => {
                    if (!shouldSend(event)) return;
                    event.preventDefault();
                    void submit();
                  }}
                >
                  <TextArea
                    label="メッセージ"
                    value={body()}
                    onInput={setBody}
                    rows={2}
                    hint={`${sendKeyLabel()} で送信`}
                  />
                </div>
                <HStack gap={2} justify="end">
                  <Button
                    variant="primary"
                    loading={sending()}
                    disabled={body().trim() === ""}
                    onClick={submit}
                  >
                    送信
                  </Button>
                </HStack>
              </Stack>
            </Show>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
